import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, Loader2, CheckCircle2, AlertTriangle, Phone, Copy, Download } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Line = ({ label, value, testid }) => (
  <div className="flex items-baseline gap-2 py-1 border-b border-[#F4F2ED] last:border-0">
    <span className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A] w-[160px] shrink-0">{label}</span>
    <span className="text-[12.5px] text-[#1A1A1A] break-all" data-testid={testid}>{value}</span>
  </div>
);

/* Recordings arrive in a Drive folder from the phone system. Access is one share
   with the service account we already hold — no new credentials, and no Google
   account needed by support staff. */
export default function CallImportCard() {
  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(true);
  const [folder, setFolder] = useState("");
  const [agent, setAgent] = useState("");
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/call-import/health`);
      setH(data);
      setFolder(data?.config?.folder_id || "");
      setAgent(data?.config?.default_agent || "");
    } catch (e) {
      setH({ configured: false, detail: e?.response?.data?.detail || "Health check failed" });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveFolder = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/call-import/folder`, { folder, default_agent: agent });
      toast.success(data.folder_id
        ? `Reading ${data.folder_name} — ${data.pending} new recording(s)`
        : "Folder cleared");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not use that folder");
    } finally { setSaving(false); }
  };

  const runImport = async () => {
    setRunning(true); setLastRun(null);
    try {
      const { data } = await axios.post(`${API}/call-import/run`, { limit: 10 }, { timeout: 600000 });
      setLastRun(data);
      toast.success(data.started
        ? `${data.started} recording(s) importing — transcription has started`
        : (data.detail || "Nothing new to import"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The import failed");
    } finally { setRunning(false); }
  };

  if (loading) {
    return <Card className="border-[#DAD5CA]"><CardContent className="p-5">
      <Loader2 className="w-4 h-4 animate-spin text-[#8A857A]" data-testid="call-import-loading" />
    </CardContent></Card>;
  }

  const ok = h?.ready;
  const email = h?.service_account || "";

  return (
    <Card className="border-[#DAD5CA]" data-testid="call-import-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <Phone className="w-4 h-4 mt-0.5 text-[#1A1A1A]" />
            <div>
              <h3 className="text-[15px] font-bold text-[#1A1A1A]">Call recordings (Google Drive)</h3>
              <p className="text-[12.5px] text-[#59554D] mt-0.5 max-w-[540px]">
                Imports new recordings from a Drive folder, transcribes them with speaker
                separation, works out whether the call was support or sales, and files it
                against the client it belongs to.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="border-[#DAD5CA]"
                  data-testid="call-import-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>

        <div className={`mt-4 rounded-lg border p-3 ${ok ? "border-[#B9CBB4] bg-[#F3F7F1]" : "border-[#E5C98A] bg-[#FDF8EC]"}`}>
          <p className="text-[12.5px] font-semibold inline-flex items-center gap-1.5" data-testid="call-import-status">
            {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-[#3F6B35]" /> : <AlertTriangle className="w-3.5 h-3.5 text-[#8A6D00]" />}
            {ok
              ? `Reading ${h.folder?.name} — ${h.pending} new, ${h.already_imported} already imported`
              : !h?.configured ? "No Google credential on the server"
                : !h?.config?.folder_id ? "Not connected to a folder yet"
                  : "Folder set, but not readable"}
          </p>
          {h?.detail && <p className="text-[12.5px] text-[#59554D] mt-1 break-words" data-testid="call-import-detail">{h.detail}</p>}
        </div>

        {email && (
          <div className="mt-4 rounded-lg border border-[#DAD5CA] bg-[#FBFAF7] p-3">
            <p className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A]">How to give access</p>
            <ol className="text-[12.5px] text-[#59554D] mt-1.5 space-y-1 list-decimal pl-4">
              <li>Open the recordings folder in Google Drive.</li>
              <li>Share → paste the address below → set to <strong>Viewer</strong> → Send.</li>
              <li>Copy the folder's link from the address bar and paste it here.</li>
            </ol>
            <div className="flex items-center gap-2 mt-2">
              <code className="mono text-[11.5px] bg-[#F4F2ED] rounded px-2 py-1 break-all" data-testid="call-import-sa-email">{email}</code>
              <Button variant="outline" size="sm" className="border-[#DAD5CA] shrink-0"
                      onClick={() => { navigator.clipboard.writeText(email); toast.success("Address copied"); }}
                      data-testid="call-import-copy-email">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}

        <div className="mt-4 space-y-2">
          <p className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A]">Drive folder</p>
          <Input value={folder} onChange={(e) => setFolder(e.target.value)}
                 placeholder="paste the folder link, or its id"
                 className="border-[#DAD5CA] text-[13px]" data-testid="call-import-folder-input" />
          <p className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A] pt-1">Agent for imported calls (optional)</p>
          <Input value={agent} onChange={(e) => setAgent(e.target.value)}
                 placeholder="left blank, each call is assigned by hand"
                 className="border-[#DAD5CA] text-[13px]" data-testid="call-import-agent-input" />
          <div className="flex items-center gap-2 pt-1">
            <Button onClick={saveFolder} disabled={saving} className="bg-[#1A1A1A] text-white"
                    data-testid="call-import-save-folder">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}
              Save folder
            </Button>
            <Button onClick={runImport} disabled={running || !ok}
                    className="bg-[#FFD108] text-black hover:bg-[#E0B800] font-semibold"
                    data-testid="call-import-run">
              {running ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-2" />}
              {running ? "Importing…" : "Import up to 10 now"}
            </Button>
          </div>
          <p className="text-[11.5px] text-[#8A857A]">
            Ten at a time: each call costs transcription time, so a folder of history
            trickles in rather than arriving at once.
          </p>
        </div>

        <div className="mt-4">
          <Line label="Service account" value={email || "—"} testid="call-import-account" />
          <Line label="Folder" value={h?.folder?.name || h?.config?.folder_id || "not set"} testid="call-import-folder-name" />
          <Line label="Waiting to import" value={ok ? h.pending : "—"} testid="call-import-pending" />
          <Line label="Last import" value={h?.config?.last_run_at
            ? `${new Date(h.config.last_run_at).toLocaleString()} · ${h.config.last_run_imported || 0} imported${h.config.last_run_errors ? `, ${h.config.last_run_errors} failed` : ""}`
            : "never"} testid="call-import-last-run" />
        </div>

        {lastRun?.results?.length > 0 && (
          <div className="mt-3 rounded-lg border border-[#DAD5CA] bg-[#FBFAF7] p-3" data-testid="call-import-results">
            <p className="text-[12px] text-[#8A857A]">
              {lastRun.started} started{lastRun.remaining ? ` · ${lastRun.remaining} still waiting` : ""}
            </p>
            <div className="mt-1.5 space-y-1">
              {lastRun.results.map((r, i) => (
                <p key={i} className="text-[12px] text-[#59554D] break-all" data-testid={`call-import-result-${i}`}>
                  {r.imported ? "✓" : r.skipped ? "–" : "✗"} {r.drive_name || r.name}
                  {r.direction ? ` · ${r.direction}` : ""}
                  {r.skipped ? ` · ${r.skipped}` : ""}
                  {r.error ? ` · ${r.error}` : ""}
                </p>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
